import { unlockFocus } from '../../utils/focus-lock'
import { enableScroll } from 'application/utils/helpers'
import { ModalConfig } from './config'
import type { ModalStack } from './stack'
import type { ModalListeners } from './listeners'
import type { ICloseModalOptions } from './types'

/**
 * Класс для управления закрытием модальных окон.
 */
export class ModalCloser {
  /**
   * Закрывает модальное окно.
   * @param {ICloseModalOptions} options - Опции закрытия.
   * @param {ModalStack} modalStack - Стек модальных окон.
   * @param {ModalListeners} listeners - Слушатели событий.
   * @param {Function} openModalFn - Функция открытия модального окна.
   */
  public static close(
    options: ICloseModalOptions,
    modalStack: ModalStack,
    listeners: ModalListeners,
    openModalFn: (_modalName: string) => void
  ): void {
    const {
      modalName,
      clearStack = true,
      checkModalBack = true,
      isModalBack = false
    } = options

    const modal = document.querySelector(
      `[data-modal-name="${modalName}"]`
    ) as HTMLElement

    if (!modal || !modal.classList.contains('is-active')) {
      return
    }

    const config = ModalConfig.get(modalName)

    if (!config) {
      return
    }

    modal.classList.remove('is-active')
    listeners.removeModalListeners(modal, config.openOver)

    if (config.lockFocus) {
      unlockFocus(config.focusBack)
    }

    config.closeCallback?.()

    setTimeout(() => {
      enableScroll()
    }, config.eventTimeout)

    if (modalStack.getCurrent() === modalName) {
      modalStack.setCurrent(null)
    }

    if (config.openOver) {
      modalStack.pop()
      modalStack.setCurrent(
        modalStack.getLength() > 0 ? modalStack.getCurrent() : null
      )

      setTimeout(() => {
        modal.style.zIndex = ''
      }, 300)

      return
    }

    // check if the closeModal needs to work with stack
    if (checkModalBack) {
      if (isModalBack) {
        // pop once for the current modal
        modalStack.pop()

        // check if there is a previous modal
        if (!modalStack.isEmpty()) {
          const prevModalName = modalStack.pop()
          if (prevModalName) {
            openModalFn(prevModalName)
          }
        }
      } else if (clearStack) {
        modalStack.clear()
      }
    }
  }
}
